import { useState, useCallback, useMemo } from 'react';
import { getStoreBySiteId, productUrl, STORES } from '../lib/stores';
import type { StoreConfig } from '../lib/stores';
import {
  validateCoupon,
  type ValidateCouponResult,
  type CouponLineResult,
  type CouponLineInput,
} from '../lib/coupons';
import type { AvailabilityResult } from '../hooks/useAvailabilityCheck';
import type { Customer } from '../lib/customerAuth';
import { customerDisplayName } from '../lib/customerAuth';
import StoreGroupHeader from './StoreGroupHeader';
import InlineLogin from './InlineLogin';
import InlineRegister from './InlineRegister';

interface CartItem {
  sku: string;
  title: string;
  price: number;
  brand?: string;
  size?: string;
  photoUrl?: string;
  siteName: string;
  siteId: number;
}

interface CartStepProps {
  items: CartItem[];
  onRemoveItem: (sku: string) => void;
  onClearCart: () => void;
  onProceed: () => void;
  couponApplied: ValidateCouponResult | null;
  onCouponApplied: (result: ValidateCouponResult | null) => void;
  couponError: string | null;
  onCouponError: (error: string | null) => void;
  customerEmail: string;
  customerId: number | null;
  shippingMethod: string;
  baseShippingCents: number;
  lastAddedSiteId: number | null;
  getItemStatus: (sku: string, siteId: number) => AvailabilityResult | undefined;
  allAvailable: boolean;
  checking: boolean;
  customer: Customer | null;
  onLogin: (email: string, password: string) => Promise<{
    success: boolean;
    error?: string;
    needsVerification?: boolean;
  }>;
  onRegister: (data: {
    email: string;
    password: string;
    firstName?: string;
    lastName?: string;
  }) => Promise<{ success: boolean; error?: string }>;
  onLogout: () => void;
}

const VI_LOGO_URL = '/logos/vi-consign.png';

function groupItemsByStore(items: CartItem[]): [number, CartItem[]][] {
  const bySite = new Map<number, CartItem[]>();
  for (const item of items) {
    const list = bySite.get(item.siteId) || [];
    list.push(item);
    bySite.set(item.siteId, list);
  }
  return [...bySite.entries()].sort(([aId], [bId]) => {
    const orderA = getStoreBySiteId(aId)?.sortOrder ?? 99;
    const orderB = getStoreBySiteId(bId)?.sortOrder ?? 99;
    return orderA - orderB;
  });
}

export default function CartStep({
  items,
  onRemoveItem,
  onClearCart,
  onProceed,
  couponApplied,
  onCouponApplied,
  couponError,
  onCouponError,
  customerEmail,
  customerId,
  shippingMethod,
  baseShippingCents,
  lastAddedSiteId,
  getItemStatus,
  allAvailable,
  checking,
  customer,
  onLogin,
  onRegister,
  onLogout,
}: CartStepProps) {
  const [couponInput, setCouponInput] = useState(couponApplied?.code || '');
  const [couponBusy, setCouponBusy] = useState(false);
  const [authView, setAuthView] = useState<'hidden' | 'login' | 'register'>('hidden');

  const groupedItems = useMemo(() => groupItemsByStore(items), [items]);

  const couponLines: CouponLineInput[] = useMemo(
    () =>
      items.map((item) => ({
        sku: item.sku,
        site_id: item.siteId,
        price_cents: Math.round(item.price * 100),
      })),
    [items],
  );

  const lineDiscountBySku = useMemo(() => {
    const map = new Map<string, CouponLineResult>();
    if (couponApplied?.ok && couponApplied.benefit_type === 'item' && couponApplied.lines) {
      for (const line of couponApplied.lines) {
        map.set(line.sku, line);
      }
    }
    return map;
  }, [couponApplied]);

  const merchandiseDiscount =
    couponApplied?.ok && couponApplied.benefit_type === 'item'
      ? (couponApplied.discount_cents || 0) / 100
      : 0;

  const subtotal = items.reduce((sum, item) => sum + item.price, 0);

  const contactValid = customerEmail.trim().length > 0 && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(customerEmail);

  const clearCoupon = useCallback(() => {
    onCouponApplied(null);
    onCouponError(null);
    setCouponInput('');
  }, [onCouponApplied, onCouponError]);

  const applyCoupon = useCallback(async () => {
    const code = couponInput.trim();
    if (!code) return;
    if (!contactValid) {
      onCouponError('Enter your email on the next step before applying a coupon.');
      return;
    }
    setCouponBusy(true);
    onCouponError(null);
    try {
      const result = await validateCoupon({
        code,
        email: customerEmail.trim(),
        customer_id: customerId,
        shipping_method: shippingMethod,
        shipping_cents: baseShippingCents,
        lines: couponLines,
      });
      if (!result.ok) {
        onCouponApplied(null);
        onCouponError(result.message || "This code isn't valid.");
        return;
      }
      onCouponApplied(result);
      onCouponError(null);
    } catch {
      onCouponError('Could not validate coupon. Please try again.');
      onCouponApplied(null);
    } finally {
      setCouponBusy(false);
    }
  }, [couponInput, contactValid, customerEmail, customerId, shippingMethod, baseShippingCents, couponLines, onCouponApplied, onCouponError]);

  const lastAddedStore = lastAddedSiteId ? getStoreBySiteId(lastAddedSiteId) : null;

  return (
    <>
      <div className="card">
        <div className="cart-auth-bar">
          {customer ? (
            <div className="cart-auth-signed-in">
              <span className="cart-auth-pill">
                Signed in as <strong>{customerDisplayName(customer)}</strong>
              </span>
              <button type="button" className="inline-auth-link-btn" onClick={onLogout}>
                Sign out
              </button>
            </div>
          ) : (
            <div className="cart-auth-signed-out">
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                onClick={() => setAuthView(authView === 'login' ? 'hidden' : 'login')}
              >
                Sign In
              </button>
            </div>
          )}
        </div>

        {!customer && authView === 'login' && (
          <InlineLogin
            compact
            onLogin={onLogin}
            onSwitchToRegister={() => setAuthView('register')}
            onCancel={() => setAuthView('hidden')}
          />
        )}
        {!customer && authView === 'register' && (
          <InlineRegister
            onRegister={onRegister}
            onSwitchToLogin={() => setAuthView('login')}
            onCancel={() => setAuthView('hidden')}
          />
        )}

        <div className="cart-title-row">
          <h2>
            Your Cart ({items.length} item{items.length !== 1 ? 's' : ''})
          </h2>
          {lastAddedStore ? (
            <a
              href={lastAddedStore.storefrontUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="btn btn-keep-shopping"
            >
              Keep Shopping
            </a>
          ) : (
            <div className="store-links-inline">
              {STORES.map((store) => (
                <a
                  key={store.siteId}
                  href={store.storefrontUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="store-link-small"
                  title={store.name}
                >
                  <img src={store.logoUrl} alt={store.name} className="store-link-logo-small" />
                </a>
              ))}
            </div>
          )}
        </div>

        {groupedItems.map(([siteId, storeItems]) => {
          const store: StoreConfig =
            getStoreBySiteId(siteId) || {
              siteId,
              name: storeItems[0]?.siteName || 'Store',
              logoUrl: VI_LOGO_URL,
              storefrontUrl: '',
              sortOrder: 99,
            };

          return (
            <section className="store-group" key={siteId}>
              <StoreGroupHeader store={store} itemCount={storeItems.length} />

              {storeItems.map((item) => {
                const disc = lineDiscountBySku.get(item.sku);
                const hasPartial =
                  couponApplied?.ok &&
                  couponApplied.benefit_type === 'item' &&
                  (couponApplied.discount_cents || 0) > 0;
                const status = getItemStatus(item.sku, item.siteId);
                const itemUnavailable = status && !status.available;

                return (
                  <div className={`cart-item${itemUnavailable ? ' cart-item--unavailable' : ''}`} key={item.sku}>
                    {item.photoUrl ? (
                      <img className="cart-item-photo" src={item.photoUrl} alt={item.title} />
                    ) : (
                      <div className="cart-item-photo cart-item-photo-placeholder">
                        &#x1F4F7;
                      </div>
                    )}
                    <div className="cart-item-info">
                      <a
                        className="cart-item-title"
                        href={productUrl(item.siteId, item.sku)}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {item.title || item.sku}
                      </a>
                      <div className="cart-item-meta">
                        {[item.brand, item.size].filter(Boolean).join(' \u00B7 ')}
                      </div>
                      <div className="cart-item-meta">SKU: {item.sku}</div>
                      {itemUnavailable && (
                        <div className="cart-item-meta availability-warning">
                          {status.reason === 'sold'
                            ? 'This item has been sold'
                            : status.reason === 'reserved'
                              ? 'This item is reserved by another shopper'
                              : 'This item is no longer available'}
                        </div>
                      )}
                      {disc?.eligible && disc.discount_cents > 0 && (
                        <div className="cart-item-meta coupon-line-note">
                          Coupon {couponApplied?.code}
                        </div>
                      )}
                      {hasPartial && disc && !disc.eligible && (
                        <div className="cart-item-meta coupon-ineligible">
                          Not eligible for this code
                        </div>
                      )}
                      <button className="cart-item-remove" onClick={() => onRemoveItem(item.sku)}>
                        Remove
                      </button>
                    </div>
                    <div className="cart-item-price">
                      {disc?.eligible && disc.discount_cents > 0 ? (
                        <>
                          <span className="price-original">
                            ${(disc.original_cents / 100).toFixed(2)}
                          </span>
                          <span className="price-discount">
                            &minus;${(disc.discount_cents / 100).toFixed(2)}
                          </span>
                          <span className="price-final">
                            ${(disc.final_cents / 100).toFixed(2)}
                          </span>
                        </>
                      ) : (
                        <>${item.price.toFixed(2)}</>
                      )}
                    </div>
                  </div>
                );
              })}
            </section>
          );
        })}
      </div>

      <div className="card">
        <h2>Coupon Code</h2>
        <div className="coupon-row">
          <input
            type="text"
            placeholder="Coupon code"
            value={couponInput}
            onChange={(e) => setCouponInput(e.target.value.toUpperCase())}
            disabled={!!couponApplied?.ok}
            aria-label="Coupon code"
          />
          {couponApplied?.ok ? (
            <button type="button" className="coupon-btn" onClick={clearCoupon}>
              Remove
            </button>
          ) : (
            <button
              type="button"
              className="coupon-btn"
              onClick={applyCoupon}
              disabled={couponBusy || !couponInput.trim()}
            >
              {couponBusy ? '\u2026' : 'Apply'}
            </button>
          )}
        </div>
        {couponError && <p className="coupon-error">{couponError}</p>}
        {couponApplied?.ok && <p className="coupon-success">{couponApplied.message}</p>}
      </div>

      <div className="card">
        <h2>Subtotal</h2>
        <div className="cart-summary">
          <span>Items ({items.length})</span>
          <span>${subtotal.toFixed(2)}</span>
        </div>
        {merchandiseDiscount > 0 && (
          <>
            <div className="cart-summary discount">
              <span>Coupon {couponApplied?.code}</span>
              <span>&minus;${merchandiseDiscount.toFixed(2)}</span>
            </div>
            {couponApplied?.scope_note && (
              <p className="cart-summary-note">{couponApplied.scope_note}</p>
            )}
          </>
        )}
        <div className="cart-summary total">
          <span>Subtotal</span>
          <span className="total-price">
            ${Math.max(0, subtotal - merchandiseDiscount).toFixed(2)} CAD
          </span>
        </div>
        <p className="cart-summary-note">Taxes and shipping calculated on next step.</p>
      </div>

      {!allAvailable && (
        <div className="status-banner error">
          Some items in your cart are no longer available. Please remove them to continue.
        </div>
      )}

      <div className="step-actions">
        <button
          className="btn btn-primary"
          onClick={onProceed}
          disabled={!allAvailable || checking || items.length === 0}
        >
          {checking ? 'Checking availability\u2026' : 'Proceed to Shipping'}
        </button>
        <button className="btn btn-secondary" onClick={onClearCart}>
          Clear Cart
        </button>
      </div>
    </>
  );
}
